import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import {
  getAllCourses,
  getAllConcentrations,
  getAllAasPrograms,
  getTransferMappings,
  getAllInstitutions,
  getPathwayRelationships
} from '../../services/academicService'
import {
  Library,
  GraduationCap,
  LayoutDashboard,
  TableProperties,
  CheckCircle2,
  AlertCircle,
  Search
} from 'lucide-react'
import SemesterTimeline from '../../components/advising/SemesterTimeline'
import TransferMatrix from '../../components/advising/TransferMatrix'
import CourseDetailModal from '../../components/modals/CourseDetailModal'
import { toast } from 'react-toastify'
import { isSuperAdmin } from '../../utils/roleUtils'
import './styles.scss'

export default function AcademicAdvising() {
  const { user } = useAuth()
  const [courses, setCourses] = useState([])
  const [concentrations, setConcentrations] = useState([])
  const [institutions, setInstitutions] = useState([])
  const [aasPrograms, setAasPrograms] = useState([])
  const [selectedInstitution, setSelectedInstitution] = useState(user?.organizationShortName || 'all')
  const [selectedAas, setSelectedAas] = useState(null)
  const [selectedBas, setSelectedBas] = useState(null)
  const [pathwayRelations, setPathwayRelations] = useState([])
  const [mappings, setMappings] = useState([])
  const [activeView, setActiveView] = useState('roadmap') // roadmap or matrix
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [detailCourse, setDetailCourse] = useState(null)
  const [requirementTitle, setRequirementTitle] = useState('')

  useEffect(() => {
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    try {
      setLoading(true)
      const [coursesRes, instRes, aasRes, relationsRes] = await Promise.all([
        getAllCourses(),
        getAllInstitutions(),
        getAllAasPrograms(),
        getPathwayRelationships()
      ])
      setCourses(coursesRes)
      setInstitutions(instRes)
      setAasPrograms(aasRes)
      setPathwayRelations(relationsRes)

      const concRes = await getAllConcentrations('all')
      setConcentrations(concRes)
    } catch (error) {
      console.error('Failed to fetch initial academic data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedInstitution) {
      fetchConcentrations()
    }
  }, [selectedInstitution])

  const fetchConcentrations = async () => {
    try {
      const concRes = await getAllConcentrations(selectedInstitution)
      setConcentrations(concRes)
      if (selectedBas && !concRes.find(c => c.id === selectedBas.id)) {
        setSelectedBas(null)
      }
    } catch (error) {
      console.error('Failed to filter concentrations:', error)
    }
  }

  useEffect(() => {
    if (selectedAas && selectedBas) {
      fetchMappings()
    }
  }, [selectedAas, selectedBas])

  useEffect(() => {
    // Selection Sync: If the current target goal is no longer compatible with the new source, reset it
    if (selectedAas && selectedBas && pathwayRelations.length > 0) {
      const isCompatible = pathwayRelations.some(
        rel => rel.aasProgramId === selectedAas.id && rel.basConcentrationId === selectedBas.id
      );
      if (!isCompatible) {
        setSelectedBas(null);
      }
    }
  }, [selectedAas, pathwayRelations, selectedBas]);

  const fetchMappings = async () => {
    try {
      console.log(`[Mappings] Fetching for AAS: ${selectedAas.name}, BAS: ${selectedBas.name}`);
      const data = await getTransferMappings(selectedAas.id, selectedBas.id)
      setMappings(data)
    } catch (error) {
      console.error('Failed to fetch mappings:', error)
    }
  }

  const generateRoadmap = () => {
    if (!selectedAas || !selectedBas) return [];
    const roadmap = [];
    // Semesters 1-4 (AAS Foundation)
    const aasCourses = selectedAas.courses || [];
    const aasPerSemester = Math.ceil(aasCourses.length / 4) || 3;

    for (let i = 0; i < 4; i++) {
      const semesterNum = i + 1;
      const startIdx = i * aasPerSemester;
      const semCourses = aasCourses.slice(startIdx, startIdx + aasPerSemester).map(c => ({
        code: c.code,
        title: c.title,
        credits: c.credits || 3,
        status: 'transferred'
      }));

      roadmap.push({
        name: `Semester ${semesterNum} (AAS)`,
        status: 'complete',
        courses: semCourses
      });
    }

    // Semesters 5-8 (BAS Completion)
    // Intelligently combine core and foundation for BAS completion
    const upperCore = selectedBas.upperDivisionCore || [];
    const foundation = selectedBas.foundationCourses || [];
    
    // Merge and deduplicate courses for the BAS portion
    const allBasCourses = [...upperCore, ...foundation].filter((c, index, self) => 
      c && c.courseCode && self.findIndex(t => t.courseCode === c.courseCode) === index
    );

    const coursesPerSemester = Math.ceil(allBasCourses.length / 4) || 4;

    for (let i = 0; i < 4; i++) {
      const semesterNum = i + 5;
      const startIdx = i * coursesPerSemester;
      const semCourses = allBasCourses.slice(startIdx, startIdx + coursesPerSemester).map(c => {
        // Check if this course code is in our mappings as transposed/transferred
        const isTransferred = (mappings || []).some(m => 
          m.courseCode === c.courseCode || m.courseTitle?.includes(c.courseCode)
        );
        return {
          code: c.courseCode || 'TBD',
          title: c.notes || 'Course Requirement',
          credits: c.credits || 3,
          status: isTransferred ? 'transferred' : 'remaining'
        };
      });

      roadmap.push({
        name: `Semester ${semesterNum} (BAS)`,
        status: 'pending',
        courses: semCourses
      });
    }

    console.debug(`[Roadmap] Generated ${roadmap.length} semesters. Upper Core Courses: ${upperCore.length}`);
    return roadmap;
  };

  const handleCourseClick = (roadmapCourse) => {
    if (!roadmapCourse || !roadmapCourse.code) return;

    const searchCode = roadmapCourse.code.replace(/\s/g, '').toUpperCase();

    // Attempt to find full course intelligence from our ingested database
    const matched = courses.find(c => {
      if (!c.code) return false;
      return c.code.replace(/\s/g, '').toUpperCase() === searchCode;
    });

    // Merge roadmap metadata with matched course intelligence
    // This ensures credits (like 4.0) from the roadmap flow into the modal
    setDetailCourse(matched ? { ...roadmapCourse, ...matched } : roadmapCourse);
    setRequirementTitle(roadmapCourse.title);
    setIsModalOpen(true);
  };

  return (
    <div className="academic-advising-page">
      <div className="hub-centered-container">
        <header className="page-header-premium">
          <div className="hero-section">
            <GraduationCap className="header-icon-large" />
            <h1>Academic Intelligence Hub</h1>
            <p className="subtitle">
              {selectedInstitution !== 'all' ? `${selectedInstitution} ` : ''}
              BAS Pathway Visualization & Advisor Engine
            </p>
          </div>



          <div className="view-selector-pill">
            <button
              className={`tab-btn ${activeView === 'roadmap' ? 'active' : ''}`}
              onClick={() => setActiveView('roadmap')}
            >
              <LayoutDashboard size={18} />
              Semester Roadmap
            </button>
            <button
              className={`tab-btn ${activeView === 'matrix' ? 'active' : ''}`}
              onClick={() => setActiveView('matrix')}
            >
              <TableProperties size={18} />
              Transfer Matrix
            </button>
          </div>
        </header>

        <main className="advising-content">
          <div className="selection-bar-glass">
            {user?.organizationName && (
              <div className="org-display-pill">
                <Library size={18} />
                <span>{user.organizationName}</span>
              </div>
            )}
            
            <div className="selectors-row">
              <div className="select-group">
                <label>Source AAS Program</label>
                <select 
                  value={selectedAas?.id || ''}
                  onChange={(e) => setSelectedAas(aasPrograms.find(p => p.id === e.target.value))}
                >
                  <option value="">Select Student Source...</option>
                  {(() => {
                    const groups = aasPrograms.reduce((acc, p) => {
                      const group = p.schoolName || p.school || 'General Institution';
                      if (!acc[group]) acc[group] = [];
                      acc[group].push(p);
                      return acc;
                    }, {});

                    return Object.keys(groups).sort().map(groupName => (
                      <optgroup label={groupName} key={groupName}>
                        {groups[groupName]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))
                        }
                      </optgroup>
                    ));
                  })()}
                </select>
              </div>
              <div className="select-group">
                <label>Target BAS Concentration</label>
                <select
                  value={selectedBas?.id || ''}
                  onChange={(e) => setSelectedBas(concentrations.find(c => c.id === e.target.value))}
                >
                  <option value="">Select Goal...</option>
                  {(() => {
                    if (!selectedAas) return concentrations;
                    return concentrations.filter(c => 
                      pathwayRelations.some(rel => rel.aasProgramId === selectedAas.id && rel.basConcentrationId === c.id)
                    );
                  })().map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-state-premium">
              <div className="pulse-loader"></div>
              <p>Gathering Curricular Intelligence...</p>
            </div>
          ) : (
            <div className="view-container">
              {(!selectedAas || !selectedBas) ? (
                <div className="empty-state-card-glass">
                  <AlertCircle size={48} className="empty-icon" />
                  <h3>Pathway Selection Required</h3>
                  <p>Please select both a source AAS program and a target BAS concentration above to visualize the academic mapping and semester roadmap.</p>
                </div>
              ) : (mappings.length === 0 && activeView === 'matrix') ? (
                <div className="empty-state-card-glass warning">
                  <Search size={48} className="empty-icon text-amber-500" />
                  <h3>No Official Mapping Discovered</h3>
                  <p>
                    We currently do not have a registered transfer matrix for <strong>{selectedAas.name}</strong> to <strong>{selectedBas.name}</strong>.
                    Please consult an academic advisor for personalized evaluation.
                  </p>
                </div>
              ) : (
                <>
                  <section className="view-header-row">
                    <div className="view-title-group">
                       <h2>{selectedInstitution !== 'all' ? selectedInstitution : 'BAS'} {activeView === 'roadmap' ? 'Projected Roadmap' : 'Mapping Matrix'}</h2>
                    </div>
                    {activeView === 'roadmap' && (
                      <div className="roadmap-legend-glass">
                        <div className="legend-item">
                          <span className="dot transferred"></span>
                          <label>AAS Transferred</label>
                        </div>
                        <div className="legend-item">
                          <span className="dot remaining"></span>
                          <label>Remaining Requirement</label>
                        </div>
                      </div>
                    )}
                  </section>

                  {activeView === 'roadmap' ? (
                    <section className="roadmap-view">
                      <div className="info-card-glass">
                        <div className="roadmap-container">
                          <SemesterTimeline 
                            data={generateRoadmap()} 
                            onCourseClick={handleCourseClick}
                          />
                        </div>
                      </div>
                    </section>
                  ) : (
                    <section className="matrix-view">
                      <div className="info-card-glass">
                        <div className="matrix-container">
                          <TransferMatrix mappings={mappings} basName={selectedBas?.name} />
                        </div>
                      </div>
                    </section>
                  )
                  }
                </>
              )}
            </div>
          )}
        </main>
      </div>

      <CourseDetailModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        course={detailCourse}
        requirementTitle={requirementTitle}
      />
    </div>
  )
}
